/**
 * Cloud diagram storage service using Supabase
 */

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState } from "@excalidraw/excalidraw/types";

import { isSupabaseConfigured, supabase } from "../supabaseClient";

export interface ChatMessageAttachmentMeta {
  filename: string;
  mimeType: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string; // ISO string for JSON serialization
  imageCount?: number;
  attachmentFiles?: ChatMessageAttachmentMeta[];
}

export interface DiagramRecord {
  id: string;
  user_id: string;
  name: string;
  elements: ExcalidrawElement[];
  app_state: Partial<AppState> | null;
  chat_history: ChatMessage[];
  chart_type: string;
  thumbnail: string | null;
  created_at: string;
  updated_at: string;
}

export interface DiagramListItem {
  id: string;
  name: string;
  thumbnail: string | null;
  updated_at: string;
}

export interface SaveDiagramParams {
  id?: string;
  name?: string;
  elements: readonly ExcalidrawElement[];
  appState?: Partial<AppState>;
  chatHistory?: ChatMessage[];
  chartType?: string;
  thumbnail?: string;
}

/**
 * Get all diagrams for the current user
 */
export const getDiagrams = async (): Promise<DiagramListItem[]> => {
  if (!isSupabaseConfigured || !supabase) {
    console.warn("[DiagramStorage] Supabase not configured");
    return [];
  }

  // Debug: Get current user to verify auth state
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError && import.meta.env.DEV) {
    console.error("[DiagramStorage] Auth error:", userError);
  }

  if (!user) {
    console.warn(
      "[DiagramStorage] No authenticated user, returning empty list",
    );
    return [];
  }

  const { data, error } = await supabase
    .from("diagrams")
    .select("id, name, thumbnail, updated_at")
    .order("updated_at", { ascending: false });

  if (error) {
    console.error("[DiagramStorage] Failed to fetch diagrams:", error);
    throw error;
  }

  return data || [];
};

/**
 * Get a single diagram by ID
 */
export const getDiagram = async (id: string): Promise<DiagramRecord | null> => {
  if (!isSupabaseConfigured || !supabase) {
    console.warn("[DiagramStorage] Supabase not configured");
    return null;
  }

  const { data, error } = await supabase
    .from("diagrams")
    .select("id, user_id, name, elements, app_state, chat_history, chart_type, thumbnail, created_at, updated_at")
    .eq("id", id)
    .single();

  if (error) {
    console.error("[DiagramStorage] Failed to fetch diagram:", error);
    throw error;
  }

  return data;
};

export interface DiagramElementsOnly {
  elements: ExcalidrawElement[];
  app_state: Partial<AppState> | null;
}

/**
 * Get only elements and app_state for thumbnail generation (avoids fetching
 * chat_history, thumbnail, and other large columns).
 */
export const getDiagramForThumbnail = async (
  id: string,
): Promise<DiagramElementsOnly | null> => {
  if (!isSupabaseConfigured || !supabase) {
    return null;
  }

  const { data, error } = await supabase
    .from("diagrams")
    .select("elements, app_state")
    .eq("id", id)
    .single();

  if (error) {
    console.error("[DiagramStorage] Failed to fetch diagram for thumbnail:", error);
    throw error;
  }

  return data;
};

export interface DiagramCreateResult {
  id: string;
  name: string;
  chat_history: ChatMessage[];
  created_at: string;
  updated_at: string;
}

/**
 * Create a new diagram
 */
export const createDiagram = async (
  params: SaveDiagramParams,
): Promise<DiagramCreateResult> => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase not configured");
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error("User not authenticated");
  }

  const { data, error } = await supabase
    .from("diagrams")
    .insert({
      user_id: user.id,
      name: params.name || "Untitled Diagram",
      elements: params.elements,
      app_state: params.appState || {},
      chat_history: params.chatHistory || [],
      chart_type: params.chartType || "auto",
      thumbnail: params.thumbnail || null,
    })
    .select("id, name, chat_history, created_at, updated_at")
    .single();

  if (error) {
    console.error("[DiagramStorage] Failed to create diagram:", error);
    throw error;
  }

  return data;
};

export interface DiagramUpdateResult {
  id: string;
  updated_at: string;
}

/**
 * Update an existing diagram
 */
export const updateDiagram = async (
  id: string,
  params: Partial<SaveDiagramParams>,
): Promise<DiagramUpdateResult> => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase not configured");
  }

  const updateData: Record<string, unknown> = {};

  if (params.name !== undefined) {
    updateData.name = params.name;
  }
  if (params.elements !== undefined) {
    updateData.elements = params.elements;
  }
  if (params.appState !== undefined) {
    updateData.app_state = params.appState;
  }
  if (params.chatHistory !== undefined) {
    updateData.chat_history = params.chatHistory;
  }
  if (params.chartType !== undefined) {
    updateData.chart_type = params.chartType;
  }
  if (params.thumbnail !== undefined) {
    updateData.thumbnail = params.thumbnail;
  }

  const { data, error } = await supabase
    .from("diagrams")
    .update(updateData)
    .eq("id", id)
    .select("id, updated_at")
    .single();

  if (error) {
    console.error("[DiagramStorage] Failed to update diagram:", error);
    throw error;
  }

  return data;
};

/**
 * Delete a diagram
 */
export const deleteDiagram = async (id: string): Promise<void> => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase not configured");
  }

  const { error } = await supabase.from("diagrams").delete().eq("id", id);

  if (error) {
    console.error("[DiagramStorage] Failed to delete diagram:", error);
    throw error;
  }
};

/**
 * Save or update a diagram (upsert logic)
 */
export const saveDiagram = async (
  params: SaveDiagramParams,
): Promise<DiagramCreateResult | DiagramUpdateResult> => {
  if (params.id) {
    return updateDiagram(params.id, params);
  }
  return createDiagram(params);
};
